import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';
import {
  Badge,
  Button,
  Callout,
  GeneralList,
  GeneralListSortableTh,
  Label,
  NumberInput,
  StatusDotPill,
  Textarea,
  TextInput,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../components/ui';
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon, DocumentTextIcon, DownloadIcon, LockIcon, PrinterIcon, SaveIcon, UnlockIcon, UserIcon, UserPlusIcon, ViewColumnsIcon, XIcon } from '../../components/ui/Icons';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '../../components/ui/Modal';
import { Column } from '../../components/DataTable/DataTable';
import { formatDate as formatDateGlobal, formatDateTime } from '../../config/appConfig';
import {
  closeMgaPeriod,
  generateMgaPeriod,
  importMgaFromPrior,
  reopenMgaPeriod,
  updateMgaPeriod,
  updateMgaPeriodLines,
} from '../../services/mutations';
import {
  formatMgaStatus,
  moduleConfig,
  statusBadgeVariant,
} from './config';
import {
  useMgaBondedLines,
  useMgaCashEntries,
  useMgaPeriod,
  type MgaColumnDef,
  type MgaCrewLine,
  type MgaPeriod,
  type MgaSettlementColumnDef,
} from './useData';
import {
  isDeductionListColumn,
  isEditableSettlementColumn,
  settlementColumnDefsForGrid,
  settlementColumnHeader,
  settlementValueFromLine,
} from './settlementColumns';
import {
  BONDED_INCOMING_CODE,
  CASH_INCOME_CODE,
} from './booksCategories';
import { BooksTab } from './BooksTab';
import { PeriodFilesPanel } from './PeriodFilesPanel';
import {
  buildMgaCloseChecklist,
  carryForwardSummary,
  type MgaCloseCheckItem,
} from './closeChecklist';
import { MGA_REPORT_KEYS, MGA_REPORT_META } from './reports/reportMeta';

type CellEditField =
  | 'daysInPeriod'
  | 'periodAmount'
  | 'remarks'
  | 'extraEarnings'
  | 'previousBalance'
  | 'overtimeHours'
  | 'overtimeRate'
  | 'cashAdvanceOffice'
  | 'cashAdvanceMaster'
  | 'allotmentAmount'
  | 'cigarettesAmount'
  | 'internetCardsAmount'
  | 'otherBondedAmount'
  | 'variousDeductions'
  | 'requestedAllotment'
  | 'requestedCashByMaster'
  | (string & {});

type CellEditTarget = {
  lineId: number;
  crewName: string;
  field: CellEditField;
  wageValueId?: number;
  columnCode: string;
  columnName: string;
  valueLabel: string;
  value: string;
  inputKind: 'number' | 'text';
};

type DraftLine = {
  id: number;
  daysInPeriod: string;
  extraEarnings: string;
  previousBalance: string;
  overtimeHours: string;
  overtimeRate: string;
  cashAdvanceOffice: string;
  cashAdvanceMaster: string;
  allotmentAmount: string;
  cigarettesAmount: string;
  internetCardsAmount: string;
  otherBondedAmount: string;
  variousDeductions: string;
  requestedAllotment: string;
  requestedCashByMaster: string;
  remarks: string;
  periodAmounts: Record<number, string>;
};

function formatAmt(n: number | null | undefined) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(Number(n));
}

/** Always two decimal places (e.g. 5,000.00). */
function formatMoney(n: number | null | undefined) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n));
}

function dangerAmt(isDark: boolean) {
  return isDark ? 'text-red-400' : 'text-red-600';
}

function approvalUserName(
  user?: {
    userDisplayName?: string | null;
    userFirstName?: string;
    userLastName?: string;
  } | null,
) {
  if (!user) return null;
  const display = user.userDisplayName?.trim();
  if (display) return display;
  return [user.userFirstName, user.userLastName].filter(Boolean).join(' ').trim() || null;
}

const CLOSE_APPROVAL_STATUS_LABEL: Record<
  NonNullable<MgaPeriod['closeApproval']>['status'],
  string
> = {
  DRAFT: 'Draft',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

const CLOSE_APPROVAL_STATUS_DOT: Record<
  NonNullable<MgaPeriod['closeApproval']>['status'],
  string
> = {
  DRAFT: 'bg-slate-400',
  PENDING: 'bg-amber-500',
  APPROVED: 'bg-emerald-500',
  REJECTED: 'bg-red-500',
  CANCELLED: 'bg-slate-400',
};

const CLOSE_STEP_STATUS_DOT: Record<
  NonNullable<MgaPeriod['closeApproval']>['steps'][number]['status'],
  string
> = {
  PENDING: 'bg-amber-500',
  APPROVED: 'bg-emerald-500',
  REJECTED: 'bg-red-500',
  SKIPPED: 'bg-slate-400',
};

function lineToDraft(line: MgaCrewLine): DraftLine {
  const periodAmounts: Record<number, string> = {};
  for (const v of line.wageValues ?? []) {
    periodAmounts[v.id] = v.periodAmount != null ? String(v.periodAmount) : '';
  }
  const val = (code: string) => String(settlementValueFromLine(line, code));
  return {
    id: line.id,
    daysInPeriod: String(line.daysInPeriod ?? 0),
    extraEarnings: val('extraEarnings'),
    previousBalance: val('previousBalance'),
    overtimeHours: val('overtimeHours'),
    overtimeRate: val('overtimeRate'),
    cashAdvanceOffice: val('cashAdvanceOffice'),
    cashAdvanceMaster: val('cashAdvanceMaster'),
    allotmentAmount: val('allotmentAmount'),
    cigarettesAmount: val('cigarettesAmount'),
    internetCardsAmount: val('internetCardsAmount'),
    otherBondedAmount: val('otherBondedAmount'),
    variousDeductions: val('variousDeductions') || String(line.deductions ?? 0),
    requestedAllotment: val('requestedAllotment'),
    requestedCashByMaster: val('requestedCashByMaster'),
    remarks: line.remarks ?? '',
    periodAmounts,
  };
}

function columnDefsForGrid(period: MgaPeriod | undefined): MgaColumnDef[] {
  const defs = period?.columnDefs ?? [];
  if (defs.length) {
    return defs
      .slice()
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.code.localeCompare(b.code));
  }
  const map = new Map<string, MgaColumnDef>();
  for (const line of period?.lines ?? []) {
    for (const v of line.wageValues ?? []) {
      if (!map.has(v.columnCode)) {
        map.set(v.columnCode, {
          id: v.columnId ?? 0,
          code: v.columnCode,
          name: v.columnName,
          columnType: v.columnType,
          includeInTotal: v.includeInTotal,
          sortOrder: 0,
          isActive: true,
        });
      }
    }
  }
  return Array.from(map.values());
}

function errMsg(err: unknown, fallback: string) {
  const msg =
    (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
  return Array.isArray(msg) ? msg.join(' ') : typeof msg === 'string' ? msg : fallback;
}

const PeriodGrid: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { showError, showSuccess } = useNotification();
  const { can } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const periodId = id ? parseInt(id, 10) : NaN;
  const { item, isLoading, mutate } = useMgaPeriod(Number.isInteger(periodId) ? periodId : 0);
  const { data: cashEntries } = useMgaCashEntries(Number.isInteger(periodId) ? periodId : 0);
  const { data: bondedLines } = useMgaBondedLines(Number.isInteger(periodId) ? periodId : 0);
  const canEdit =
    can(moduleConfig.module.permissionModuleKey, 'edit') || can('vessel_mga', 'edit');
  const canClose =
    can(moduleConfig.module.permissionModuleKey, 'close') || can('vessel_mga', 'close');
  const locked = item?.status === 'CLOSED';
  const editable = Boolean(canEdit && !locked && item?.status === 'OPEN');
  const closeApprovalStatus = item?.closeApproval?.status ?? null;
  const needsCloseResubmit = closeApprovalStatus === 'DRAFT';
  const closeApprovalPending = closeApprovalStatus === 'PENDING';
  const canSubmitClose = Boolean(canClose && item?.status === 'OPEN' && !closeApprovalPending);

  const tabRaw = searchParams.get('tab') || 'payroll';
  const tab =
    tabRaw === 'requests' ||
    tabRaw === 'deductions' ||
    tabRaw === 'cash' ||
    tabRaw === 'bonded'
      ? 'books'
      : tabRaw;
  const setTab = (t: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', t);
    if (t !== 'books') next.delete('category');
    setSearchParams(next, { replace: true });
  };
  const deductionCategory = searchParams.get('category') || '';
  const setDeductionCategory = (code: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'books');
    next.set('category', code);
    setSearchParams(next, { replace: true });
  };

  // Legacy Cash / Bonded / Deductions / Crew requests top tabs → Books
  useEffect(() => {
    if (
      tabRaw !== 'cash' &&
      tabRaw !== 'bonded' &&
      tabRaw !== 'requests' &&
      tabRaw !== 'deductions'
    ) {
      return;
    }
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', 'books');
        if (tabRaw === 'cash' && !next.get('category')) next.set('category', CASH_INCOME_CODE);
        if (tabRaw === 'bonded' && !next.get('category')) next.set('category', BONDED_INCOMING_CODE);
        return next;
      },
      { replace: true },
    );
  }, [tabRaw, setSearchParams]);

  const [drafts, setDrafts] = useState<Record<number, DraftLine>>({});
  const [dirty, setDirty] = useState(false);
  const [notesDraft, setNotesDraft] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [masterSignName, setMasterSignName] = useState('');
  const [cellEdit, setCellEdit] = useState<CellEditTarget | null>(null);
  const [cellEditValue, setCellEditValue] = useState('');
  const [printOpen, setPrintOpen] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);
  /** When false, wage-scale columns collapse into a single Wage total. */
  const [showWageScale, setShowWageScale] = useState(() => {
    try {
      return sessionStorage.getItem('mga-show-wage-scale') === '1';
    } catch {
      return false;
    }
  });
  const [payrollSearch, setPayrollSearch] = useState('');
  const [payrollPage, setPayrollPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const { sortConfig, handleSort } = useGeneralListSort({ key: 'rankSortOrder', direction: 'asc' });

  const setShowWageScalePersist = useCallback((next: boolean) => {
    setShowWageScale(next);
    try {
      sessionStorage.setItem('mga-show-wage-scale', next ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!printOpen) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (printRef.current?.contains(t)) return;
      setPrintOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [printOpen]);

  useEffect(() => {
    if (!item?.lines) return;
    const next: Record<number, DraftLine> = {};
    for (const line of item.lines) next[line.id] = lineToDraft(line);
    setDrafts(next);
    setDirty(false);
  }, [item]);

  useEffect(() => {
    if (!item) return;
    setNotesDraft(item.notes ?? '');
    setNotesDirty(false);
  }, [item?.id, item?.notes]);

  const wageColumns = useMemo(
    () => columnDefsForGrid(item).filter((c) => c.columnType !== 'INFO'),
    [item],
  );
  const settlementColumns = useMemo(
    () => settlementColumnDefsForGrid(item?.settlementColumnDefs),
    [item?.settlementColumnDefs],
  );
  const lines = item?.lines ?? [];
  const totals = item?.totals;

  const closeChecklist = useMemo(
    () =>
      buildMgaCloseChecklist({
        lines,
        cash: cashEntries,
        bonded: bondedLines,
      }),
    [lines, cashEntries, bondedLines],
  );

  const carryForward = useMemo(
    () =>
      carryForwardSummary({
        previousBalance: totals?.previousBalance,
        cash: cashEntries,
        bonded: bondedLines,
      }),
    [totals?.previousBalance, cashEntries, bondedLines],
  );

  const goToChecklistItem = (itemCheck: MgaCloseCheckItem) => {
    setCloseConfirmOpen(false);
    setMasterSignName('');
    if (!itemCheck.tab) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', itemCheck.tab);
    if (itemCheck.tab === 'books' && itemCheck.category) {
      next.set('category', itemCheck.category);
    } else {
      next.delete('category');
    }
    setSearchParams(next, { replace: true });
  };

  const filteredPayrollLines = useMemo(() => {
    const q = payrollSearch.trim().toLowerCase();
    let rows = lines;
    if (q) {
      rows = lines.filter((line) => {
        const hay = [line.crewName, line.rankName, line.nationalityName, line.remarks]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return hay.includes(q);
      });
    }
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const key = sortConfig.key;
    return [...rows].sort((a, b) => {
      if (key === 'rankSortOrder') {
        const ao = a.rankSortOrder ?? 9999;
        const bo = b.rankSortOrder ?? 9999;
        if (ao !== bo) return (ao - bo) * dir;
        return String(a.crewName ?? '').localeCompare(String(b.crewName ?? '')) * dir;
      }
      if (key === 'rankName') {
        const cmp = String(a.rankName ?? '').localeCompare(String(b.rankName ?? ''), undefined, {
          sensitivity: 'base',
        });
        if (cmp !== 0) return cmp * dir;
        return (a.rankSortOrder ?? 9999) - (b.rankSortOrder ?? 9999);
      }
      if (key === 'nationalityName') {
        return (
          String(a.nationalityName ?? '').localeCompare(String(b.nationalityName ?? ''), undefined, {
            sensitivity: 'base',
          }) * dir
        );
      }
      const av =
        key === 'daysInPeriod'
          ? Number(drafts[a.id]?.daysInPeriod ?? a.daysInPeriod) || 0
          : key === 'netDue'
            ? Number(a.netDue) || 0
            : String(a.crewName ?? '').toLowerCase();
      const bv =
        key === 'daysInPeriod'
          ? Number(drafts[b.id]?.daysInPeriod ?? b.daysInPeriod) || 0
          : key === 'netDue'
            ? Number(b.netDue) || 0
            : String(b.crewName ?? '').toLowerCase();
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv)) * dir;
    });
  }, [lines, payrollSearch, sortConfig, drafts]);

  const payrollTotalPages = Math.max(1, Math.ceil(filteredPayrollLines.length / itemsPerPage) || 1);

  useEffect(() => {
    setPayrollPage(1);
  }, [payrollSearch, itemsPerPage, sortConfig.key, sortConfig.direction]);

  useEffect(() => {
    if (payrollPage > payrollTotalPages) setPayrollPage(payrollTotalPages);
  }, [payrollPage, payrollTotalPages]);

  const payrollPageRows = useMemo(() => {
    const start = (payrollPage - 1) * itemsPerPage;
    return filteredPayrollLines.slice(start, start + itemsPerPage);
  }, [filteredPayrollLines, payrollPage, itemsPerPage]);

  const payrollAaById = useMemo(() => {
    const map = new Map<number, number>();
    filteredPayrollLines.forEach((line, i) => map.set(line.id, i + 1));
    return map;
  }, [filteredPayrollLines]);

  const hasPayrollSearch = payrollSearch.trim().length > 0;

  const updateDraft = useCallback((lineId: number, patch: Partial<DraftLine>) => {
    setDrafts((prev) => ({
      ...prev,
      [lineId]: { ...prev[lineId], ...patch },
    }));
    setDirty(true);
  }, []);

  const updatePeriodAmount = useCallback((lineId: number, wageValueId: number, value: string) => {
    setDrafts((prev) => {
      const cur = prev[lineId];
      if (!cur) return prev;
      return {
        ...prev,
        [lineId]: {
          ...cur,
          periodAmounts: { ...cur.periodAmounts, [wageValueId]: value },
        },
      };
    });
    setDirty(true);
  }, []);

  const openCellEdit = useCallback(
    (target: CellEditTarget) => {
      if (!editable) return;
      setCellEdit(target);
      setCellEditValue(target.value);
    },
    [editable],
  );

  const closeCellEdit = useCallback(() => {
    setCellEdit(null);
    setCellEditValue('');
  }, []);

  const applyCellEdit = useCallback(() => {
    if (!cellEdit) return;
    if (cellEdit.field === 'periodAmount') {
      if (cellEdit.wageValueId != null) {
        updatePeriodAmount(cellEdit.lineId, cellEdit.wageValueId, cellEditValue);
      }
    } else {
      updateDraft(cellEdit.lineId, { [cellEdit.field]: cellEditValue });
    }
    closeCellEdit();
  }, [cellEdit, cellEditValue, updateDraft, updatePeriodAmount, closeCellEdit]);

  const editableCellClass = editable
    ? cn(colors.info, 'cursor-pointer select-none rounded px-1.5 py-0.5 -mx-0.5')
    : '';

  const tableColumns = useMemo((): Column<MgaCrewLine>[] => {
    const cols: Column<MgaCrewLine>[] = [
      {
        key: 'aa',
        header: 'A/A',
        align: 'right',
        render: (_v, row) => (
          <span className={cn('block text-sm tabular-nums text-right', colors.textMuted)}>
            {payrollAaById.get(row.id) ?? '—'}
          </span>
        ),
      },
      {
        key: 'crewName',
        header: 'Crew',
        render: (_v, row) => {
          const on = row.signOnDate ? formatDateGlobal(row.signOnDate) : '—';
          const off = row.signOffDate ? formatDateGlobal(row.signOffDate) : '—';
          return (
            <div className="flex min-w-[11rem] max-w-[16rem] items-start gap-1.5">
              <Link
                to={`/mga/${periodId}/lines/${row.id}`}
                className={cn(
                  'inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors',
                  colors.primaryFg,
                  colors.interactiveHover,
                )}
                title="Monthly pay account"
                aria-label={`Open monthly pay account for ${row.crewName}`}
              >
                <DocumentTextIcon className="h-4 w-4" />
              </Link>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className={cn('text-sm font-medium leading-snug', colors.text)}>
                    {row.crewName}
                  </span>
                  {row.missingWageSplit && (
                    <Badge variant="secondary" className="rounded-md px-1.5 py-0 text-[10px]">
                      No wage split
                    </Badge>
                  )}
                </div>
                <p className={cn('mt-0.5 text-[11px] tabular-nums leading-snug', colors.textMuted)}>
                  {on} → {off}
                </p>
              </div>
            </div>
          );
        },
        footerType: 'custom',
        footerRender: () =>
          totals ? <span className="font-semibold">Totals ({totals.lineCount})</span> : null,
      },
      {
        key: 'rankName',
        header: 'Rank',
        render: (_v, row) => (
          <span className={cn('text-sm whitespace-nowrap', colors.text)}>
            {row.rankName || '—'}
          </span>
        ),
      },
      {
        key: 'nationalityName',
        header: 'Nationality',
        render: (_v, row) => (
          <span className={cn('text-sm whitespace-nowrap', colors.textMuted)}>
            {row.nationalityName || '—'}
          </span>
        ),
      },
    ];

    // Days + dynamic wages + settlement need per-row draft; build via render that reads drafts
    cols.push({
      key: 'daysInPeriod',
      header: 'Days',
      align: 'right',
      render: (_v, row) => {
        const draft = drafts[row.id] ?? lineToDraft(row);
        return (
          <span
            className={cn('block text-sm tabular-nums text-right', colors.text, editableCellClass)}
            title={editable ? 'Double-click to edit' : undefined}
            onDoubleClick={(e) => {
              e.stopPropagation();
              openCellEdit({
                lineId: row.id,
                crewName: row.crewName,
                field: 'daysInPeriod',
                columnCode: 'Days',
                columnName: 'Days in period',
                valueLabel: 'Days',
                value: draft.daysInPeriod,
                inputKind: 'number',
              });
            }}
          >
            {formatAmt(draft.daysInPeriod === '' ? null : Number(draft.daysInPeriod))}
          </span>
        );
      },
    });

    const buildWageCol = (col: (typeof wageColumns)[number]): Column<MgaCrewLine> => {
      const isAllotCol = col.code.toUpperCase() === 'ALLOT';
      return {
        key: `wage:${col.code}`,
        header: col.code,
        align: 'right',
        footerType: isAllotCol ? 'custom' : undefined,
        footerRender: isAllotCol
          ? () => (
              <span className={dangerAmt(isDark)}>
                {formatMoney(totals?.allotmentAmount)}
              </span>
            )
          : undefined,
        render: (_v, row) => {
          // ALLOT = sum of allotment requests for this crew (synced into allotmentAmount)
          if (isAllotCol) {
            const draft = drafts[row.id] ?? lineToDraft(row);
            const amt = Number(draft.allotmentAmount) || Number(row.allotmentAmount) || 0;
            return (
              <span
                className={cn('block text-sm tabular-nums text-right', dangerAmt(isDark))}
                title="Sum of allotment requests"
              >
                {formatMoney(amt)}
              </span>
            );
          }
          const draft = drafts[row.id] ?? lineToDraft(row);
          const wv = (row.wageValues ?? []).find((x) => x.columnCode === col.code);
          const displayValue = wv
            ? draft.periodAmounts[wv.id] ?? (wv.periodAmount != null ? String(wv.periodAmount) : '')
            : '';
          const canOpenEdit = Boolean(wv && editable);
          return (
            <span
              className={cn(
                'block text-sm tabular-nums text-right',
                col.columnType === 'DEDUCTION' ? dangerAmt(isDark) : colors.text,
                canOpenEdit && editableCellClass,
              )}
              title={canOpenEdit ? 'Double-click to edit' : undefined}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (!wv || !editable) return;
                openCellEdit({
                  lineId: row.id,
                  crewName: row.crewName,
                  field: 'periodAmount',
                  wageValueId: wv.id,
                  columnCode: col.code,
                  columnName: col.name,
                  valueLabel: 'Period amount',
                  value: displayValue,
                  inputKind: 'number',
                });
              }}
            >
              {wv ? formatMoney(displayValue === '' ? null : Number(displayValue)) : '—'}
            </span>
          );
        },
      };
    };

    const afterExtraCodes = new Set(['ALLOT', 'OTHER_DED']);
    const wageScaleCols = wageColumns.filter(
      (c) => !afterExtraCodes.has(c.code.toUpperCase()),
    );

    const rowWageScaleTotal = (row: MgaCrewLine) => {
      const draft = drafts[row.id] ?? lineToDraft(row);
      let sum = 0;
      for (const col of wageScaleCols) {
        if (col.columnType === 'DEDUCTION') continue;
        const wv = (row.wageValues ?? []).find((x) => x.columnCode === col.code);
        if (!wv || !col.includeInTotal) continue;
        const raw =
          draft.periodAmounts[wv.id] ??
          (wv.periodAmount != null ? String(wv.periodAmount) : '');
        sum += Number(raw) || 0;
      }
      return sum;
    };

    if (showWageScale) {
      wageScaleCols.forEach((col, idx) => {
        const base = buildWageCol(col);
        if (idx === 0) {
          cols.push({
            ...base,
            header: (
              <span className="inline-flex items-center gap-1.5">
                <button
                  type="button"
                  className={cn(
                    'inline-flex items-center rounded p-0.5 hover:opacity-80',
                    colors.primaryFg,
                  )}
                  title="Hide wage scale columns"
                  aria-label="Hide wage scale columns"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowWageScalePersist(false);
                  }}
                >
                  <ChevronLeftIcon className="h-3.5 w-3.5" />
                </button>
                <span>{col.code}</span>
              </span>
            ),
          });
        } else {
          cols.push(base);
        }
      });
    } else if (wageScaleCols.length) {
      cols.push({
        key: 'wageScaleTotal',
        header: (
          <button
            type="button"
            className={cn(
              'inline-flex items-center gap-1 uppercase tracking-wider hover:underline',
              colors.primaryFg,
            )}
            title="Show individual wage columns"
            onClick={(e) => {
              e.stopPropagation();
              setShowWageScalePersist(true);
            }}
          >
            Wage total
            <ChevronRightIcon className="h-3.5 w-3.5" />
          </button>
        ),
        align: 'right',
        footerType: 'custom',
        footerRender: (_v, all) =>
          formatMoney(all.reduce((s, row) => s + rowWageScaleTotal(row), 0)),
        render: (_v, row) => {
          const total = rowWageScaleTotal(row);
          const parts = wageScaleCols
            .filter((c) => c.columnType !== 'DEDUCTION' && c.includeInTotal)
            .map((c) => {
              const draft = drafts[row.id] ?? lineToDraft(row);
              const wv = (row.wageValues ?? []).find((x) => x.columnCode === c.code);
              if (!wv) return null;
              const raw =
                draft.periodAmounts[wv.id] ??
                (wv.periodAmount != null ? String(wv.periodAmount) : '');
              const n = Number(raw) || 0;
              if (!n) return null;
              return `${c.code} ${formatMoney(n)}`;
            })
            .filter(Boolean);
          return (
            <button
              type="button"
              className={cn(
                'block w-full text-sm font-medium tabular-nums text-right hover:underline',
                colors.text,
              )}
              title={
                parts.length
                  ? `${parts.join(' · ')} — click to show columns`
                  : 'Click to show wage columns'
              }
              onClick={(e) => {
                e.stopPropagation();
                setShowWageScalePersist(true);
              }}
            >
              {formatMoney(total)}
            </button>
          );
        },
      });
    }

    const settlement = (
      field: Exclude<CellEditField, 'periodAmount' | 'remarks' | 'daysInPeriod'>,
      col: MgaSettlementColumnDef,
      footer?: number | null,
      red?: boolean,
      money = true,
    ): Column<MgaCrewLine> => {
      const header = settlementColumnHeader(col);
      const fmt = money ? formatMoney : formatAmt;
      const editableCol = editable && isEditableSettlementColumn(col);
      const booksManaged = isDeductionListColumn(col);
      return {
        key: field,
        header,
        align: 'right',
        footerType: footer != null ? 'custom' : undefined,
        footerRender: footer != null ? () => fmt(footer) : undefined,
        render: (_v, row) => {
          const draft = drafts[row.id] ?? lineToDraft(row);
          const draftVal = String(draft[field as keyof DraftLine] ?? '');
          return (
            <span
              className={cn(
                'block text-sm tabular-nums text-right',
                red ? dangerAmt(isDark) : colors.text,
                editableCol ? editableCellClass : '',
              )}
              title={
                editableCol
                  ? 'Double-click to edit'
                  : booksManaged
                    ? 'Managed in Books — not editable here'
                    : col.name
              }
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (!editableCol) return;
                openCellEdit({
                  lineId: row.id,
                  crewName: row.crewName,
                  field,
                  columnCode: col.code,
                  columnName: col.name,
                  valueLabel: col.name,
                  value: draftVal,
                  inputKind: 'number',
                });
              }}
            >
              {fmt(draftVal === '' ? null : Number(draftVal))}
            </span>
          );
        },
      };
    };

    const footerForCode = (code: string): number | null | undefined => {
      if (code === 'previousBalance') return totals?.previousBalance;
      if (code === 'extraEarnings') return totals?.extraEarnings;
      return undefined;
    };

    const hasOtCols =
      settlementColumns.some((c) => c.code === 'overtimeHours') &&
      settlementColumns.some((c) => c.code === 'overtimeRate');

    for (const col of settlementColumns) {
      if (col.code === 'overtimeHours') {
        cols.push(
          settlement('overtimeHours', col, undefined, false, false),
        );
        continue;
      }
      if (col.code === 'overtimeRate') {
        cols.push(settlement('overtimeRate', col));
        if (hasOtCols) {
          cols.push({
            key: 'otTotal',
            header: 'OT Total',
            align: 'right',
            footerType: 'custom',
            footerRender: () => formatMoney(totals?.otTotal),
            render: (_v, row) => {
              const draft = drafts[row.id] ?? lineToDraft(row);
              const ot =
                (Number(draft.overtimeHours) || 0) * (Number(draft.overtimeRate) || 0);
              return (
                <span className={cn('text-sm tabular-nums whitespace-nowrap', colors.text)}>
                  {formatMoney(ot)}
                </span>
              );
            },
          });
        }
        continue;
      }
      if (col.code === 'extraEarnings') {
        cols.push(settlement('extraEarnings', col, footerForCode('extraEarnings')));
        for (const code of ['ALLOT', 'OTHER_DED'] as const) {
          const wageCol = wageColumns.find((c) => c.code.toUpperCase() === code);
          if (wageCol) cols.push(buildWageCol(wageCol));
        }
        continue;
      }
      if (col.code === 'cashAdvanceMaster') {
        cols.push({
          key: 'cashAdvanceMaster',
          header: settlementColumnHeader(col),
          align: 'right',
          render: (_v, row) => {
            const draft = drafts[row.id] ?? lineToDraft(row);
            const amt =
              Number(draft.cashAdvanceMaster) || Number(row.cashAdvanceMaster) || 0;
            return (
              <span
                className={cn('block text-sm tabular-nums text-right', dangerAmt(isDark))}
                title="Sum of cash-by-master requests + cash-book expenses linked to this crew"
              >
                {formatMoney(amt)}
              </span>
            );
          },
        });
        continue;
      }
      // Books-managed and other non-manual columns still show when on the grid; editability is gated above.
      const isDeduction = col.columnType === 'DEDUCTION';
      cols.push(
        settlement(
          col.code as Exclude<CellEditField, 'periodAmount' | 'remarks' | 'daysInPeriod'>,
          col,
          footerForCode(col.code),
          isDeduction,
        ),
      );
    }

    cols.push(
      {
        key: 'totalDeductions',
        header: 'Tot. Ded.',
        align: 'right',
        footerType: 'custom',
        footerRender: () => (
          <span className={dangerAmt(isDark)}>
            {formatMoney(totals?.totalDeductions ?? totals?.deductions)}
          </span>
        ),
        render: (_v, row) => (
          <span className={cn('text-sm tabular-nums whitespace-nowrap', dangerAmt(isDark))}>
            {formatMoney(row.totalDeductions ?? row.deductions)}
          </span>
        ),
      },
      {
        key: 'netDue',
        header: 'Balance',
        align: 'right',
        footerType: 'custom',
        footerRender: () => (
          <span className="font-semibold">{formatMoney(totals?.netDue)}</span>
        ),
        render: (v) => (
          <span className={cn('text-sm font-semibold tabular-nums whitespace-nowrap', colors.text)}>
            {formatMoney(v)}
          </span>
        ),
      },
      {
        key: 'remarks',
        header: 'Remarks',
        maxWidth: '12rem',
        render: (_v, row) => {
          const draft = drafts[row.id] ?? lineToDraft(row);
          return (
            <span
              className={cn(
                'block max-w-[12rem] truncate text-sm',
                colors.textMuted,
                editableCellClass,
              )}
              title={editable ? draft.remarks || 'Double-click to edit' : draft.remarks || undefined}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (!editable) return;
                openCellEdit({
                  lineId: row.id,
                  crewName: row.crewName,
                  field: 'remarks',
                  columnCode: 'Remarks',
                  columnName: 'Remarks',
                  valueLabel: 'Remarks',
                  value: draft.remarks,
                  inputKind: 'text',
                });
              }}
            >
              {draft.remarks || '—'}
            </span>
          );
        },
      },
    );

    return cols;
  }, [
    wageColumns,
    settlementColumns,
    drafts,
    editable,
    editableCellClass,
    openCellEdit,
    colors,
    totals,
    periodId,
    showWageScale,
    setShowWageScalePersist,
    isDark,
    payrollAaById,
  ]);

  const handleGenerate = async () => {
    if (!item || !canEdit) return;
    setBusy(true);
    try {
      const result = await generateMgaPeriod(item.id);
      const n = result.generatedCount ?? 0;
      showSuccess(n ? `Generated ${n} crew line${n === 1 ? '' : 's'}.` : 'No new crew lines to add.');
      await mutate();
    } catch (err: unknown) {
      showError(errMsg(err, 'Generate failed.'));
    } finally {
      setBusy(false);
    }
  };

  const handleImportFromPrior = async () => {
    if (!item || !canEdit || locked) return;
    setBusy(true);
    try {
      const res = await importMgaFromPrior(item.id);
      const parts = [
        res.previousBalancesUpdated
          ? `${res.previousBalancesUpdated} previous balance(s)`
          : null,
        res.bondedStockCopied ? `${res.bondedStockCopied} remaining stock line(s)` : null,
        res.openingCashCreated ? 'opening cash' : null,
      ].filter(Boolean);
      showSuccess(
        parts.length
          ? `Imported from prior CLOSED MGA: ${parts.join(', ')}.`
          : 'Prior CLOSED MGA found, but nothing new to import (balances already set / stock & cash already present).',
      );
      await mutate();
    } catch (err: unknown) {
      showError(errMsg(err, 'Import requires a previous CLOSED MGA for this vessel.'));
    } finally {
      setBusy(false);
    }
  };

  const handleSave = async () => {
    if (!item || !editable) return;

    if (tab === 'notes') {
      setBusy(true);
      try {
        await updateMgaPeriod(item.id, { notes: notesDraft.trim() || null });
        showSuccess('Notes saved.');
        setNotesDirty(false);
        await mutate();
      } catch (err: unknown) {
        showError(errMsg(err, 'Save failed.'));
      } finally {
        setBusy(false);
      }
      return;
    }

    const payload = Object.values(drafts).map((d) => ({
      id: d.id,
      daysInPeriod: Number(d.daysInPeriod) || 0,
      extraEarnings: Number(d.extraEarnings) || 0,
      previousBalance: Number(d.previousBalance) || 0,
      overtimeHours: Number(d.overtimeHours) || 0,
      overtimeRate: Number(d.overtimeRate) || 0,
      cashAdvanceOffice: Number(d.cashAdvanceOffice) || 0,
      cigarettesAmount: Number(d.cigarettesAmount) || 0,
      internetCardsAmount: Number(d.internetCardsAmount) || 0,
      otherBondedAmount: Number(d.otherBondedAmount) || 0,
      variousDeductions: Number(d.variousDeductions) || 0,
      remarks: d.remarks.trim() || null,
      wageValues: Object.entries(d.periodAmounts).map(([wid, amt]) => ({
        id: Number(wid),
        periodAmount: Number(amt) || 0,
      })),
    }));
    setBusy(true);
    try {
      await updateMgaPeriodLines(item.id, { lines: payload });
      showSuccess('Sheet saved.');
      setDirty(false);
      await mutate();
    } catch (err: unknown) {
      showError(errMsg(err, 'Save failed.'));
    } finally {
      setBusy(false);
    }
  };

  const handleClose = async () => {
    if (!item || !canSubmitClose) return;
    if (dirty || notesDirty) {
      showError('Save changes before closing the period.');
      return;
    }
    setCloseConfirmOpen(true);
  };

  const confirmClose = async () => {
    if (!item || !canSubmitClose) return;
    const name = masterSignName.trim();
    if (name.length < 2) {
      showError('Enter the master’s name / signature to close.');
      return;
    }
    setBusy(true);
    try {
      const closed = await closeMgaPeriod(item.id, { masterApprovedByName: name });
      setCloseConfirmOpen(false);
      setMasterSignName('');
      if (closed?.submittedForApproval) {
        showSuccess(
          closed?.resubmitted
            ? 'Close approval resubmitted. The period will close once approved.'
            : 'Period submitted for closing approval. It will close once approved.',
        );
      } else {
        const posting = closed?.posting as
          | { status?: string; documentId?: number; documentCode?: string | null; reason?: string }
          | undefined;
        if (posting?.status === 'posted') {
          showSuccess(
            `Period closed. Draft journal ${posting.documentCode || `#${posting.documentId}`} created.`,
          );
        } else if (posting?.status === 'skipped') {
          showSuccess(`Period closed. ${posting.reason || 'No finance journal created.'}`);
        } else {
          showSuccess('Period closed. Edits are locked until reopened.');
        }
      }
      await mutate();
    } catch (err: unknown) {
      showError(errMsg(err, needsCloseResubmit ? 'Resubmit failed.' : 'Close failed.'));
    } finally {
      setBusy(false);
    }
  };

  const handleReopen = async () => {
    if (!item || !canClose) return;
    setBusy(true);
    try {
      await reopenMgaPeriod(item.id);
      showSuccess('Period reopened.');
      await mutate();
    } catch (err: unknown) {
      showError(errMsg(err, 'Reopen failed.'));
    } finally {
      setBusy(false);
    }
  };

  if (!Number.isInteger(periodId)) {
    return (
      <div className="p-8 text-center">
        <Link to={moduleConfig.module.route.list} className={cn(colors.primaryFg, 'underline')}>
          Back to MGA
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  if (!item) {
    return (
      <div className="mx-auto max-w-lg p-8 text-center">
        <h1 className={cn('text-xl font-semibold', colors.text)}>MGA period not found</h1>
        <Link
          to={moduleConfig.module.route.list}
          className={cn('mt-4 inline-block text-sm underline', colors.primaryFg)}
        >
          Back to MGA
        </Link>
      </div>
    );
  }

  return (
    <div className={cn('flex h-full min-h-0 flex-col', colors.background)}>
      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border shadow-sm',
          colors.surface,
          colors.border,
        )}
      >
        <div className={cn('shrink-0 border-b', colors.border)}>
          <div className="flex flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 space-y-0.5">
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <Link
                  to={moduleConfig.module.route.detail(periodId)}
                  className={cn(
                    'inline-flex shrink-0 items-center gap-0.5 text-[11px] font-medium',
                    colors.textMuted,
                    'hover:underline',
                  )}
                >
                  <ChevronLeftIcon className="h-3.5 w-3.5" />
                  Dashboard
                </Link>
                <h1 className={cn('truncate text-base font-semibold tracking-tight', colors.text)}>
                  {item.vessel?.vesselName ?? `Vessel #${item.vesselId}`}
                </h1>
                <Badge variant={statusBadgeVariant(item.status)}>{formatMgaStatus(item.status)}</Badge>
                {dirty || notesDirty ? <Badge variant="secondary">Unsaved</Badge> : null}
              </div>
              <p className={cn('truncate text-xs tabular-nums', colors.textMuted)}>
                {[
                  `${formatDateGlobal(item.periodFrom)} → ${formatDateGlobal(item.periodTo)}`,
                  item.currency || null,
                  `${lines.length} crew`,
                  carryForward.hasAny
                    ? [
                        Math.abs(carryForward.previousBalance) > 0.005
                          ? `Prev. bal. ${formatMoney(carryForward.previousBalance)}`
                          : null,
                        carryForward.openingCash > 0.005
                          ? `Opening cash ${formatMoney(carryForward.openingCash)}`
                          : null,
                        carryForward.openingBonded > 0.005
                          ? `Opening bonded ${formatMoney(carryForward.openingBonded)}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 sm:justify-end">
              <div className="min-w-[6.5rem] text-left sm:text-right">
                <p className={cn('text-[10px] font-medium uppercase tracking-wide', colors.textMuted)}>
                  Net due
                </p>
                <p className={cn('text-lg font-semibold tabular-nums leading-tight', colors.text)}>
                  {formatMoney(totals?.netDue)}
                </p>
              </div>
              {(canEdit ||
                canClose ||
                (editable && (tab === 'payroll' || tab === 'books' || tab === 'notes'))) && (
                <div
                  className={cn(
                    'inline-flex flex-wrap items-center gap-1 rounded-lg border p-0.5',
                    colors.border,
                    colors.surface,
                  )}
                >
                  {canEdit && item.status !== 'CLOSED' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8"
                      disabled={busy}
                      onClick={handleImportFromPrior}
                      title="Import prior month — previous balances, remaining bonded stock, and opening cash"
                    >
                      <DownloadIcon className="h-4 w-4" />
                      Import
                    </Button>
                  )}
                  {canEdit && item.status !== 'CLOSED' && tab === 'payroll' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8"
                      disabled={busy}
                      onClick={handleGenerate}
                      title={lines.length ? 'Add missing crew' : 'Generate from crew list'}
                    >
                      <UserPlusIcon className="h-4 w-4" />
                      {lines.length ? 'Add crew' : 'Generate'}
                    </Button>
                  )}
                  {editable && (tab === 'payroll' || tab === 'books' || tab === 'notes') && (
                    <Button
                      type="button"
                      size="sm"
                      className="h-8"
                      disabled={busy || (tab === 'notes' ? !notesDirty : !dirty)}
                      onClick={handleSave}
                      title={
                        busy
                          ? 'Saving…'
                          : tab === 'notes'
                            ? 'Save notes'
                            : 'Save'
                      }
                    >
                      <SaveIcon className="h-4 w-4" />
                      {busy ? 'Saving…' : 'Save'}
                    </Button>
                  )}
                  {canSubmitClose && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8"
                      disabled={busy}
                      onClick={handleClose}
                      title={needsCloseResubmit ? 'Resubmit close for approval' : 'Close period'}
                    >
                      <LockIcon className="h-4 w-4" />
                      {needsCloseResubmit ? 'Resubmit' : 'Close'}
                    </Button>
                  )}
                  {canClose && closeApprovalPending && item.status === 'OPEN' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8"
                      disabled
                      title="Close approval already pending"
                    >
                      <LockIcon className="h-4 w-4 opacity-50" />
                      Pending
                    </Button>
                  )}
                  {canClose && item.status === 'CLOSED' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8"
                      disabled={busy}
                      onClick={handleReopen}
                      title="Reopen period"
                    >
                      <UnlockIcon className="h-4 w-4" />
                      Reopen
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="flex min-h-0 flex-1 flex-col">
          <div
            className={cn(
              'flex shrink-0 flex-wrap items-center justify-between gap-2 border-b px-3 py-1.5',
              colors.border,
              colors.headerBg,
            )}
          >
            <TabsList variant="pill" fit>
              <TabsTrigger value="payroll" appearance="pill" size="sm" colorScheme="indigo" count={lines.length}>
                Payroll
              </TabsTrigger>
              <TabsTrigger value="books" appearance="pill" size="sm" colorScheme="indigo">
                Books
              </TabsTrigger>
              <TabsTrigger value="notes" appearance="pill" size="sm" colorScheme="indigo">
                Notes
              </TabsTrigger>
              <TabsTrigger value="files" appearance="pill" size="sm" colorScheme="indigo">
                Files
                {(item.attachments?.length ?? 0) > 0 ? (
                  <span className="ml-1 tabular-nums opacity-80">({item.attachments?.length})</span>
                ) : null}
              </TabsTrigger>
            </TabsList>

            <div className={cn('relative z-50')} ref={printRef}>
              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-haspopup="menu"
                aria-expanded={printOpen}
                onClick={() => setPrintOpen((v) => !v)}
              >
                <PrinterIcon className="h-4 w-4" />
                Print
                <ChevronDownIcon
                  className={cn('h-3.5 w-3.5 transition-transform', printOpen && 'rotate-180')}
                />
              </Button>
              {printOpen ? (
                <div
                  role="menu"
                  className={cn(
                    'absolute right-0 z-50 mt-1 min-w-[15rem] overflow-hidden rounded-md border py-1 shadow-lg',
                    colors.surface,
                    colors.border,
                  )}
                >
                  {MGA_REPORT_KEYS.map((key) => (
                    <Link
                      key={key}
                      role="menuitem"
                      to={`/mga/${periodId}/reports/${key}`}
                      className={cn(
                        'flex w-full flex-col gap-0.5 px-3 py-2 text-left hover:bg-black/5 dark:hover:bg-white/10',
                        colors.text,
                      )}
                      onClick={() => setPrintOpen(false)}
                    >
                      <span className="flex items-center gap-2 text-sm font-medium">
                        <DocumentTextIcon className="h-4 w-4 shrink-0 opacity-70" />
                        {MGA_REPORT_META[key].title}
                      </span>
                      <span className={cn('pl-6 text-[11px] leading-snug', colors.textMuted)}>
                        {MGA_REPORT_META[key].subtitle}
                      </span>
                    </Link>
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          <TabsContent value="payroll" className="mt-0 flex min-h-0 flex-1 flex-col p-3">
            <GeneralList
              className="h-full min-h-0"
              density="compact"
              sorting={{ sortConfig, onSort: handleSort }}
              search={{
                value: payrollSearch,
                onChange: setPayrollSearch,
                placeholder: 'Search crew…',
                title: 'Searches name, rank, nationality, and remarks',
              }}
              actions={
                wageColumns.some((c) => !['ALLOT', 'OTHER_DED'].includes(c.code.toUpperCase())) ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10"
                    onClick={() => setShowWageScalePersist(!showWageScale)}
                    title={showWageScale ? 'Collapse wage scale columns' : 'Expand wage scale columns'}
                  >
                    <ViewColumnsIcon className="h-4 w-4" />
                    {showWageScale ? 'Collapse wages' : 'Expand wages'}
                  </Button>
                ) : null
              }
              hasFilters={hasPayrollSearch}
              onClearFilters={() => setPayrollSearch('')}
              clearFiltersLabel="Clear search"
              isEmpty={filteredPayrollLines.length === 0}
              emptyState={{
                icon: <UserIcon className="h-12 w-12" />,
                title: hasPayrollSearch
                  ? 'No matching crew'
                  : 'No crew lines yet',
                hint: hasPayrollSearch
                  ? 'Try a different search or clear it.'
                  : 'Generate from the vessel crew list for this period.',
                action: hasPayrollSearch ? (
                  <button
                    type="button"
                    onClick={() => setPayrollSearch('')}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
                      colors.border,
                      colors.text,
                    )}
                  >
                    Clear search
                  </button>
                ) : canEdit && item.status !== 'CLOSED' ? (
                  <Button type="button" size="sm" disabled={busy} onClick={handleGenerate}>
                    Generate from crew list
                  </Button>
                ) : undefined,
              }}
              pagination={
                filteredPayrollLines.length > 0
                  ? {
                      page: payrollPage,
                      totalPages: payrollTotalPages,
                      total: filteredPayrollLines.length,
                      itemsPerPage,
                      pageSizeOptions,
                      onPageChange: setPayrollPage,
                      onItemsPerPageChange: (size) => {
                        setItemsPerPage(size);
                        setPayrollPage(1);
                      },
                    }
                  : undefined
              }
            >
              <table className="min-w-max w-full text-sm">
                <thead>
                  <tr
                    className={cn(
                      'border-b text-left text-xs uppercase tracking-wide',
                      colors.border,
                      colors.textMuted,
                    )}
                  >
                    {tableColumns.map((col) => {
                      const sortKey =
                        col.key === 'crewName' ||
                        col.key === 'nationalityName' ||
                        col.key === 'daysInPeriod' ||
                        col.key === 'netDue'
                          ? String(col.key)
                          : col.key === 'rankName'
                            ? 'rankSortOrder'
                            : null;
                      const align = col.align === 'right' ? 'right' : col.align === 'center' ? 'center' : 'left';
                      if (sortKey) {
                        return (
                          <GeneralListSortableTh
                            key={String(col.key)}
                            sortKey={sortKey}
                            align={align}
                            className="whitespace-nowrap"
                            style={col.maxWidth ? { maxWidth: col.maxWidth } : undefined}
                          >
                            {col.header}
                          </GeneralListSortableTh>
                        );
                      }
                      return (
                        <th
                          key={String(col.key)}
                          className={cn(
                            'sticky top-0 z-10 whitespace-nowrap font-medium',
                            colors.surface,
                            align === 'right' && 'text-right',
                            align === 'center' && 'text-center',
                          )}
                          style={col.maxWidth ? { maxWidth: col.maxWidth } : undefined}
                        >
                          {col.header}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {payrollPageRows.map((row) => (
                    <tr
                      key={row.id}
                      className={cn('border-b last:border-b-0', colors.border, colors.interactiveHover)}
                    >
                      {tableColumns.map((col) => {
                        const raw = (row as unknown as Record<string, unknown>)[String(col.key)];
                        return (
                          <td
                            key={String(col.key)}
                            className={cn(
                              'align-top whitespace-nowrap',
                              col.align === 'right' && 'text-right',
                              col.align === 'center' && 'text-center',
                              col.maxWidth && 'max-w-[12rem] overflow-hidden',
                            )}
                            style={col.maxWidth ? { maxWidth: col.maxWidth } : undefined}
                          >
                            {col.render ? col.render(raw, row) : (raw as React.ReactNode)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                {totals ? (
                  <tfoot>
                    <tr className={cn('border-t-2 font-medium', colors.border, colors.surfaceHover)}>
                      {tableColumns.map((col) => (
                        <td
                          key={String(col.key)}
                          className={cn(
                            'sticky bottom-0 whitespace-nowrap',
                            colors.surfaceHover,
                            col.align === 'right' && 'text-right',
                            col.align === 'center' && 'text-center',
                          )}
                        >
                          {col.footerType === 'custom' && col.footerRender
                            ? col.footerRender(undefined, lines)
                            : null}
                        </td>
                      ))}
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </GeneralList>
          </TabsContent>

          <TabsContent value="books" className="mt-0 flex min-h-0 flex-1 flex-col p-3">
            <BooksTab
              periodId={periodId}
              editable={editable}
              canEdit={canEdit && !locked}
              lines={lines}
              currency={item.currency || 'USD'}
              categories={
                item.deductionListColumns?.length
                  ? item.deductionListColumns
                  : (item.settlementColumnDefs ?? []).filter(isDeductionListColumn)
              }
              activeCategory={deductionCategory}
              onCategoryChange={setDeductionCategory}
              onChanged={() => mutate()}
            />
          </TabsContent>

          <TabsContent value="notes" className="mt-0 min-h-0 flex-1 overflow-auto p-3">
            {(() => {
              const closeApproval = item.closeApproval ?? null;
              const masterName =
                item.masterApprovedByName || closeApproval?.masterApprovedByName || null;
              const closedBy = approvalUserName(item.closedByUser);
              const submittedBy = approvalUserName(closeApproval?.createdByUser);
              const periodStatusHint =
                item.status === 'CLOSED'
                  ? 'This period is locked. Notes and payroll can no longer be edited.'
                  : item.status === 'OPEN'
                    ? closeApproval?.status === 'PENDING'
                      ? 'Close approval is in progress. The period will lock once approved.'
                      : closeApproval?.status === 'DRAFT'
                        ? 'Close was returned for changes. Fix the sheet, then resubmit with the lock button (master signature).'
                        : 'Period is open for editing. Closing requires master signature and approval.'
                    : 'Draft period — generate crew lines to open it.';

              return (
                <div className="mx-auto grid max-w-5xl gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)]">
                  <section
                    className={cn(
                      'flex flex-col rounded-xl border p-5 shadow-sm',
                      colors.surface,
                      colors.border,
                    )}
                  >
                    <div className="mb-4">
                      <h2 className={cn('text-sm font-semibold', colors.text)}>Period notes</h2>
                      <p className={cn('mt-1 text-xs leading-relaxed', colors.textMuted)}>
                        Remarks for the master and office (max 250 characters). Saved separately from
                        payroll line edits.
                      </p>
                    </div>
                    <Label htmlFor="mga-period-notes" className="mb-1.5">
                      Notes
                    </Label>
                    <Textarea
                      id="mga-period-notes"
                      value={notesDraft}
                      onChange={(e) => {
                        setNotesDraft(e.target.value);
                        setNotesDirty(true);
                      }}
                      rows={12}
                      maxLength={250}
                      showCharacterCount
                      disabled={!editable}
                      readOnly={!editable}
                      placeholder={editable ? 'Add notes for this MGA period…' : 'No notes'}
                      className="min-h-[12rem] flex-1"
                    />
                    {editable && (
                      <div className="mt-4 flex justify-end">
                        <Button
                          type="button"
                          size="sm"
                          disabled={busy || !notesDirty}
                          onClick={handleSave}
                        >
                          {busy ? 'Saving…' : 'Save notes'}
                        </Button>
                      </div>
                    )}
                  </section>

                  <div className="flex flex-col gap-4">
                    <section
                      className={cn(
                        'rounded-xl border p-5 shadow-sm',
                        colors.surface,
                        colors.border,
                      )}
                    >
                      <div className="mb-4 flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <h2 className={cn('text-sm font-semibold', colors.text)}>
                            Status &amp; approval
                          </h2>
                          <p className={cn('mt-1 text-xs leading-relaxed', colors.textMuted)}>
                            Period lifecycle, master signature, and close-approval trail.
                          </p>
                        </div>
                        <Badge variant={statusBadgeVariant(item.status)}>
                          {formatMgaStatus(item.status)}
                        </Badge>
                      </div>

                      <Callout
                        variant={
                          item.status === 'CLOSED'
                            ? 'success'
                            : closeApproval?.status === 'PENDING'
                              ? 'warning'
                              : closeApproval?.status === 'DRAFT'
                                ? 'warning'
                                : closeApproval?.status === 'REJECTED'
                                  ? 'danger'
                                  : 'info'
                        }
                        title={
                          item.status === 'CLOSED'
                            ? 'Period closed'
                            : closeApproval?.status === 'PENDING'
                              ? 'Awaiting close approval'
                              : closeApproval?.status === 'DRAFT'
                                ? 'Returned for changes — resubmit from MGA'
                                : closeApproval?.status === 'REJECTED'
                                  ? 'Close approval rejected'
                                  : 'Period status'
                        }
                      >
                        {periodStatusHint}
                        {closeApproval?.status === 'DRAFT' && closeApproval.lastReturn?.comments ? (
                          <p className="mt-2 italic">"{closeApproval.lastReturn.comments}"</p>
                        ) : null}
                      </Callout>

                      <dl className="mt-4 space-y-3 text-sm">
                        <div className="flex items-start justify-between gap-3">
                          <dt className={cn('shrink-0', colors.textMuted)}>Period</dt>
                          <dd className={cn('text-right tabular-nums', colors.text)}>
                            {formatDateGlobal(item.periodFrom)} → {formatDateGlobal(item.periodTo)}
                          </dd>
                        </div>
                        <div className="flex items-start justify-between gap-3">
                          <dt className={cn('shrink-0', colors.textMuted)}>Master signature</dt>
                          <dd className={cn('text-right', colors.text)}>
                            {masterName || '—'}
                            {item.masterApprovedAt ? (
                              <span className={cn('mt-0.5 block text-xs', colors.textMuted)}>
                                {formatDateTime(item.masterApprovedAt)}
                              </span>
                            ) : null}
                          </dd>
                        </div>
                        <div className="flex items-start justify-between gap-3">
                          <dt className={cn('shrink-0', colors.textMuted)}>Closed</dt>
                          <dd className={cn('text-right', colors.text)}>
                            {item.closedAt ? formatDateTime(item.closedAt) : '—'}
                            {closedBy ? (
                              <span className={cn('mt-0.5 block text-xs', colors.textMuted)}>
                                by {closedBy}
                              </span>
                            ) : null}
                          </dd>
                        </div>
                        {item.accDocument ? (
                          <div className="flex items-start justify-between gap-3">
                            <dt className={cn('shrink-0', colors.textMuted)}>Finance doc</dt>
                            <dd className="text-right">
                              <Link
                                to={`/accounting/journals/${item.accDocument.id}`}
                                className={cn(
                                  'font-medium hover:underline',
                                  colors.primaryFg,
                                )}
                              >
                                {item.accDocument.code || `#${item.accDocument.id}`}
                              </Link>
                              <span className={cn('mt-0.5 block text-xs', colors.textMuted)}>
                                {item.accDocument.status}
                                {item.accDocument.postingDate
                                  ? ` · ${formatDateGlobal(item.accDocument.postingDate)}`
                                  : ''}
                              </span>
                            </dd>
                          </div>
                        ) : null}
                      </dl>
                    </section>

                    <section
                      className={cn(
                        'rounded-xl border p-5 shadow-sm',
                        colors.surface,
                        colors.border,
                      )}
                    >
                      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                        <h3 className={cn('text-sm font-semibold', colors.text)}>
                          Close approval
                        </h3>
                        {closeApproval ? (
                          <div className="flex flex-wrap items-center gap-2">
                            <StatusDotPill
                              dotClassName={CLOSE_APPROVAL_STATUS_DOT[closeApproval.status]}
                            >
                              {CLOSE_APPROVAL_STATUS_LABEL[closeApproval.status]}
                            </StatusDotPill>
                            <Link
                              to={`/approvals/${closeApproval.id}`}
                              className={cn(
                                'text-xs font-medium hover:underline',
                                colors.primaryFg,
                              )}
                            >
                              Open #{closeApproval.id}
                            </Link>
                          </div>
                        ) : null}
                      </div>

                      {!closeApproval ? (
                        <p className={cn('text-sm', colors.textMuted)}>
                          No close approval has been submitted for this period yet.
                        </p>
                      ) : (
                        <div className="space-y-4">
                          <dl className="space-y-2 text-sm">
                            <div className="flex items-start justify-between gap-3">
                              <dt className={cn('shrink-0', colors.textMuted)}>Type</dt>
                              <dd className={cn('text-right', colors.text)}>
                                {closeApproval.approvalType?.name ||
                                  closeApproval.approvalType?.code ||
                                  'Period close'}
                              </dd>
                            </div>
                            <div className="flex items-start justify-between gap-3">
                              <dt className={cn('shrink-0', colors.textMuted)}>Submitted</dt>
                              <dd className={cn('text-right', colors.text)}>
                                {formatDateTime(closeApproval.createdAt)}
                                {submittedBy ? (
                                  <span className={cn('mt-0.5 block text-xs', colors.textMuted)}>
                                    by {submittedBy}
                                  </span>
                                ) : null}
                              </dd>
                            </div>
                            {closeApproval.title ? (
                              <div className="flex items-start justify-between gap-3">
                                <dt className={cn('shrink-0', colors.textMuted)}>Title</dt>
                                <dd className={cn('text-right', colors.text)}>
                                  {closeApproval.title}
                                </dd>
                              </div>
                            ) : null}
                          </dl>

                          {closeApproval.steps?.length ? (
                            <ol className="space-y-2">
                              {closeApproval.steps.map((step) => {
                                const assignee =
                                  approvalUserName(step.assigneeUser) ||
                                  step.assigneeRank?.abrv ||
                                  step.assigneeRank?.name ||
                                  step.assigneeDepartment?.name ||
                                  'Unassigned';
                                const actedBy = approvalUserName(step.actedByUser);
                                return (
                                  <li
                                    key={step.id}
                                    className={cn(
                                      'rounded-lg border px-3 py-2.5',
                                      colors.border,
                                      colors.surfaceHover,
                                    )}
                                  >
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                      <div className="min-w-0">
                                        <p className={cn('text-sm font-medium', colors.text)}>
                                          {step.label || `Stage ${step.stage}`}
                                        </p>
                                        <p className={cn('text-xs', colors.textMuted)}>
                                          {assignee}
                                        </p>
                                      </div>
                                      <StatusDotPill
                                        dotClassName={CLOSE_STEP_STATUS_DOT[step.status]}
                                      >
                                        {step.status.charAt(0) +
                                          step.status.slice(1).toLowerCase()}
                                      </StatusDotPill>
                                    </div>
                                    {(actedBy || step.actedAt || step.comments) && (
                                      <p
                                        className={cn(
                                          'mt-1.5 text-xs leading-relaxed',
                                          colors.textMuted,
                                        )}
                                      >
                                        {[
                                          actedBy ? `Acted by ${actedBy}` : null,
                                          step.actedAt ? formatDateTime(step.actedAt) : null,
                                          step.comments?.trim() || null,
                                        ]
                                          .filter(Boolean)
                                          .join(' · ')}
                                      </p>
                                    )}
                                  </li>
                                );
                              })}
                            </ol>
                          ) : null}
                        </div>
                      )}
                    </section>
                  </div>
                </div>
              );
            })()}
          </TabsContent>

          <TabsContent value="files" className="mt-0 flex min-h-0 flex-1 flex-col p-3">
            <PeriodFilesPanel
              periodId={periodId}
              files={item.attachments ?? []}
              canEdit={canEdit}
              canDelete={canEdit && item.status !== 'CLOSED'}
              onChanged={() => mutate()}
              layout="workspace"
              periodClosed={item.status === 'CLOSED'}
            />
          </TabsContent>
        </Tabs>
      </div>

      <Modal isOpen={Boolean(cellEdit)} onClose={closeCellEdit} size="sm">
        <ModalHeader>
          <ModalTitle>Edit {cellEdit?.columnCode ?? 'value'}</ModalTitle>
          <Button type="button" variant="ghost" size="sm" onClick={closeCellEdit}>
            Close
          </Button>
        </ModalHeader>
        <ModalContent>
          <p className={cn('mb-3 text-sm', colors.textSecondary)}>
            {cellEdit?.crewName}
            {cellEdit?.columnName ? ` · ${cellEdit.columnName}` : ''}
          </p>
          <label className={cn('mb-1.5 block text-sm font-medium', colors.text)}>
            {cellEdit?.valueLabel ?? 'Value'}
          </label>
          {cellEdit?.inputKind === 'text' ? (
            <TextInput
              value={cellEditValue}
              onChange={(e) => setCellEditValue(e.target.value)}
              maxLength={250}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyCellEdit();
                }
              }}
            />
          ) : (
            <NumberInput
              value={cellEditValue}
              onChange={(e) => setCellEditValue(e.target.value == null ? '' : String(e.target.value))}
              allowDecimals
              showClearButton={false}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  applyCellEdit();
                }
              }}
            />
          )}
        </ModalContent>
        <ModalFooter>
          <Button type="button" variant="secondary" onClick={closeCellEdit}>
            Cancel
          </Button>
          <Button type="button" onClick={applyCellEdit}>
            Apply
          </Button>
        </ModalFooter>
      </Modal>

      <Modal
        isOpen={closeConfirmOpen}
        onClose={() => {
          setCloseConfirmOpen(false);
          setMasterSignName('');
        }}
        size="md"
      >
        <ModalHeader>
          <ModalTitle>{needsCloseResubmit ? 'Resubmit close for approval?' : 'Close MGA period?'}</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <p className={cn('mb-3 text-sm', colors.textSecondary)}>
            {needsCloseResubmit
              ? 'Office returned this close for changes. After you fix the sheet, enter the master’s name and resubmit for approval.'
              : 'Closing locks Payroll and Books (cash, bonded store, advances, allotments, deductions). Enter the master’s name as approval / signature.'}
          </p>
          <div
            className={cn(
              'mb-4 rounded-lg border px-3 py-2.5',
              colors.border,
              colors.surfaceHover,
            )}
          >
            <p className={cn('mb-2 text-xs font-semibold uppercase tracking-wide', colors.textMuted)}>
              Close checklist
            </p>
            <ul className="space-y-2">
              {closeChecklist.map((check) => {
                const ok = check.status === 'pass';
                return (
                  <li key={check.id} className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-2.5">
                      <span
                        className={cn(
                          'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full',
                          ok
                            ? isDark
                              ? 'bg-emerald-500/20 text-emerald-400'
                              : 'bg-emerald-100 text-emerald-700'
                            : isDark
                              ? 'bg-red-500/20 text-red-400'
                              : 'bg-red-100 text-red-700',
                        )}
                        aria-label={ok ? 'Pass' : 'Fail'}
                      >
                        {ok ? <CheckIcon className="h-3.5 w-3.5" /> : <XIcon className="h-3.5 w-3.5" />}
                      </span>
                      <div className="min-w-0">
                        <span className={cn('text-sm font-medium', colors.text)}>{check.label}</span>
                        {check.detail ? (
                          <p className={cn('mt-0.5 text-xs', colors.textMuted)}>{check.detail}</p>
                        ) : null}
                      </div>
                    </div>
                    {!ok && check.tab ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="shrink-0"
                        onClick={() => goToChecklistItem(check)}
                      >
                        Open
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
          <label className="block space-y-1">
            <span className={cn('text-sm font-medium', colors.text)}>Master name / signature</span>
            <TextInput
              value={masterSignName}
              onChange={(e) => setMasterSignName(e.target.value)}
              placeholder="e.g. Capt. J. Smith"
              maxLength={150}
              autoFocus
            />
          </label>
        </ModalContent>
        <ModalFooter>
          <Button
            type="button"
            variant="secondary"
            disabled={busy}
            onClick={() => {
              setCloseConfirmOpen(false);
              setMasterSignName('');
            }}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="warning"
            disabled={busy || masterSignName.trim().length < 2}
            onClick={confirmClose}
          >
            {busy
              ? needsCloseResubmit
                ? 'Resubmitting…'
                : 'Closing…'
              : needsCloseResubmit
                ? 'Resubmit for approval'
                : 'Close period'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PeriodGrid;
